import React, { useEffect, useState } from 'react';
import {
   Box,
   Typography,
   Button,
   Checkbox,
   FormControlLabel,
   TextField,
   Stepper,
   Step,
   StepLabel,
   Alert,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getAuthHeaders } from '../../utils/auth';

export interface RegisterPhotographerFormProps {
   /** When false, internal wizard resets (e.g. dialog closed). */
   active: boolean;
   defaultDisplayName?: string;
   /** Tighter spacing for embedded / card use */
   embedded?: boolean;
   onSuccess?: () => void;
   submitButtonLabel?: string;
   /** For parent dialogs that should not close while request is in flight */
   onSubmittingChange?: (submitting: boolean) => void;
}

/**
 * Same flow as public album: Terms → details → POST /api/profile/register-photographer
 * (30-day trial, email verified on server, same uid).
 */
const RegisterPhotographerForm: React.FC<RegisterPhotographerFormProps> = ({
   active,
   defaultDisplayName = '',
   embedded = false,
   onSuccess,
   submitButtonLabel = 'Add photographer account',
   onSubmittingChange,
}) => {
   const [step, setStep] = useState<'terms' | 'details'>('terms');
   const [termsAccepted, setTermsAccepted] = useState(false);
   const [submitting, setSubmitting] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [form, setForm] = useState({
      displayName: '',
      companyName: '',
      phoneNumber: '',
      description: '',
      website: '',
   });

   useEffect(() => {
      if (!active) {
         setStep('terms');
         setTermsAccepted(false);
         setError(null);
         setSubmitting(false);
         onSubmittingChange?.(false);
         setForm({
            displayName: '',
            companyName: '',
            phoneNumber: '',
            description: '',
            website: '',
         });
      }
   }, [active, onSubmittingChange]);

   useEffect(() => {
      if (active && defaultDisplayName) {
         setForm((prev) => ({
            ...prev,
            displayName: prev.displayName || defaultDisplayName,
         }));
      }
   }, [active, defaultDisplayName]);

   const tfSize = embedded ? 'medium' : 'small';
   const tfMargin = embedded ? 'normal' : 'normal';

   const handleSubmit = async () => {
      setError(null);
      setSubmitting(true);
      onSubmittingChange?.(true);
      try {
         const headers = await getAuthHeaders();
         const res = await fetch(
            `${process.env.REACT_APP_API_URL}/api/profile/register-photographer`,
            {
               method: 'POST',
               headers: { ...headers, 'Content-Type': 'application/json' },
               body: JSON.stringify({
                  displayName: form.displayName.trim(),
                  companyName: form.companyName.trim() || undefined,
                  phoneNumber: form.phoneNumber.trim() || undefined,
                  description: form.description.trim() || undefined,
                  website: form.website.trim() || undefined,
               }),
            }
         );
         const data = await res.json().catch(() => ({}));
         if (!res.ok) {
            setError(data.message || data.error || 'Failed to add photographer account');
            return;
         }
         onSuccess?.();
      } catch {
         setError('Something went wrong. Please try again.');
      } finally {
         setSubmitting(false);
         onSubmittingChange?.(false);
      }
   };

   return (
      <Box>
         <Stepper activeStep={step === 'terms' ? 0 : 1} sx={{ mb: embedded ? 2 : 2 }}>
            <Step completed={step !== 'terms'}>
               <StepLabel>Terms</StepLabel>
            </Step>
            <Step>
               <StepLabel>Your details</StepLabel>
            </Step>
         </Stepper>

         {step === 'terms' && (
            <Box>
               <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  By adding a photographer account you agree to our Terms of Service and Privacy Policy.
               </Typography>
               <FormControlLabel
                  control={
                     <Checkbox
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        color="primary"
                     />
                  }
                  label={
                     <Typography variant="body2">
                        I agree to the{' '}
                        <Typography
                           component={RouterLink}
                           to="/terms"
                           target="_blank"
                           rel="noopener noreferrer"
                           variant="body2"
                           color="primary"
                           sx={{ textDecoration: 'underline' }}
                        >
                           Terms of Service
                        </Typography>{' '}
                        and{' '}
                        <Typography
                           component={RouterLink}
                           to="/privacy"
                           target="_blank"
                           rel="noopener noreferrer"
                           variant="body2"
                           color="primary"
                           sx={{ textDecoration: 'underline' }}
                        >
                           Privacy Policy
                        </Typography>
                     </Typography>
                  }
               />
               <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                     variant="contained"
                     disabled={!termsAccepted}
                     onClick={() => setStep('details')}
                     size={embedded ? 'large' : 'medium'}
                     fullWidth={embedded}
                  >
                     Next
                  </Button>
               </Box>
            </Box>
         )}

         {step === 'details' && (
            <Box component="form" onSubmit={(e) => e.preventDefault()}>
               <TextField
                  fullWidth
                  label="Name"
                  required
                  value={form.displayName}
                  onChange={(e) => setForm((prev) => ({ ...prev, displayName: e.target.value }))}
                  margin={tfMargin}
                  size={tfSize}
               />
               <TextField
                  fullWidth
                  label="Company name"
                  value={form.companyName}
                  onChange={(e) => setForm((prev) => ({ ...prev, companyName: e.target.value }))}
                  margin={tfMargin}
                  size={tfSize}
               />
               <TextField
                  fullWidth
                  label="Phone (optional)"
                  value={form.phoneNumber}
                  onChange={(e) => setForm((prev) => ({ ...prev, phoneNumber: e.target.value }))}
                  margin={tfMargin}
                  size={tfSize}
               />
               <TextField
                  fullWidth
                  label="Description (optional)"
                  multiline
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  margin={tfMargin}
                  size={tfSize}
               />
               <TextField
                  fullWidth
                  label="Website (optional)"
                  value={form.website}
                  onChange={(e) => setForm((prev) => ({ ...prev, website: e.target.value }))}
                  margin={tfMargin}
                  size={tfSize}
               />
               {error && (
                  <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
                     {error}
                  </Alert>
               )}
               <Box
                  sx={{
                     display: 'flex',
                     flexDirection: embedded ? { xs: 'column-reverse', sm: 'row' } : 'row',
                     justifyContent: 'space-between',
                     gap: embedded ? 1.5 : 0,
                     mt: 2,
                  }}
               >
                  <Button
                     onClick={() => setStep('terms')}
                     disabled={submitting}
                     size={embedded ? 'large' : 'medium'}
                     fullWidth={embedded}
                  >
                     Back
                  </Button>
                  <Button
                     variant="contained"
                     disabled={submitting || !form.displayName?.trim()}
                     onClick={handleSubmit}
                     size={embedded ? 'large' : 'medium'}
                     fullWidth={embedded}
                  >
                     {submitting ? 'Adding…' : submitButtonLabel}
                  </Button>
               </Box>
            </Box>
         )}
      </Box>
   );
};

export default RegisterPhotographerForm;
